/**
 * Provider + model pickers for a connected model credential. Used by the agent
 * editor's model field. (The legacy evaluator form that used to share this file
 * was removed with the legacy evals.)
 */
import React, { useEffect, useRef, useState } from 'react';
import { Bot, ChevronDown, Check } from 'lucide-react';
import { MANUAL_MODEL_PROVIDERS } from 'shared/hooks/useCatalogModels';

// Mirror the icon set + labels we use in IntegrationsPanel + the workflow
// node picker so the brand is consistent across every place a credential
// can be selected.
export const EVAL_PROVIDER_ICON = {
  openai:        '/icons/model_icons/openai.svg',
  anthropic:     '/icons/model_icons/anthropic.png',
  gemini:        '/icons/model_icons/gemini.png',
  mistral:       '/icons/model_icons/mistralai.png',
  fireworks:     '/icons/model_icons/fireworksai.png',
  groq:          '/icons/model_icons/groq.png',
  deepseek:      '/icons/model_icons/deepseek.png',
  xai:           '/icons/model_icons/grok.png',
  azure_openai:  '/icons/model_icons/azureai.png',
  vertex_ai:     '/icons/model_icons/vertexai.png',
  jev: '/icons/model_icons/typesafe.png',
  trodo: '/trodologo.svg',
  bedrock:       '/icons/model_icons/bedrock.png',
};
export const EVAL_PROVIDER_LABEL = {
  openai: 'OpenAI', anthropic: 'Anthropic', gemini: 'Gemini',
  mistral: 'Mistral AI', fireworks: 'Fireworks AI', groq: 'Groq',
  deepseek: 'DeepSeek', xai: 'xAI', openai_compat: 'OpenAI-compatible endpoint',
  azure_openai: 'Azure OpenAI', vertex_ai: 'Vertex AI', bedrock: 'Amazon Bedrock',
};
const EVAL_LOGO_SCALE = { azure_openai: 1.35 };
const EVAL_NEEDS_WHITE_BG = new Set(['openai', 'xai', 'deepseek']);

export function EvalProviderLogo({ provider, size = 16 }) {
  const src = EVAL_PROVIDER_ICON[provider];
  if (!src) {
    return (
      <span style={{
        width: size, height: size, display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', background: 'var(--surface-hover)', border: '1px solid var(--border-default)',
        borderRadius: '50%', flexShrink: 0,
      }}>
        <Bot style={{ width: size - 6, height: size - 6, color: 'var(--text-muted)' }} />
      </span>
    );
  }
  const scale = EVAL_LOGO_SCALE[provider] || 1;
  const needsWhiteBg = EVAL_NEEDS_WHITE_BG.has(provider);
  return (
    <span style={{
      width: size, height: size, display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', flexShrink: 0, borderRadius: '50%',
      background: needsWhiteBg ? '#ffffff' : 'transparent',
      padding: needsWhiteBg ? 1 : 0, boxSizing: 'content-box',
    }}>
      <img src={src} alt="" style={{
        width: size - (needsWhiteBg ? 2 : 0),
        height: size - (needsWhiteBg ? 2 : 0),
        objectFit: 'contain',
        transform: scale === 1 ? undefined : `scale(${scale})`, transformOrigin: 'center',
      }} />
    </span>
  );
}

export function EvalProviderDropdown({ integrations, value, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = (integrations || []).find((i) => String(i.id) === String(value));

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="input"
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', cursor: 'pointer' }}
      >
        {selected && <EvalProviderLogo provider={selected.provider} size={16} />}
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected
            ? `${EVAL_PROVIDER_LABEL[selected.provider] || selected.provider} · ${selected.label}`
            : 'Connect a provider…'}
        </span>
        <ChevronDown style={{ width: 12, height: 12, color: 'var(--text-muted)', flexShrink: 0 }} />
      </button>
      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-[color:var(--surface-base)] border border-[color:var(--border-default)] rounded-lg shadow-lg"
          style={{ maxHeight: 280, overflowY: 'auto' }}
        >
          {(integrations || []).length === 0 && (
            <div className="px-3 py-3 text-[11.5px] text-[color:var(--text-muted)] text-center">
              No providers yet. Add one in Integrations.
            </div>
          )}
          {(integrations || []).map((i) => {
            const isSel = String(i.id) === String(value);
            return (
              <button
                key={i.id}
                type="button"
                onClick={() => { onChange(String(i.id)); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] ${isSel ? 'bg-[color:var(--surface-hover)]' : 'hover:bg-[color:var(--surface-hover)]'}`}
              >
                <EvalProviderLogo provider={i.provider} size={16} />
                <span className="flex-1 min-w-0">
                  <div className="text-[color:var(--text-primary)] truncate">{i.label}</div>
                  <div className="text-[10.5px] text-[color:var(--text-muted)] truncate">
                    {EVAL_PROVIDER_LABEL[i.provider] || i.provider} · {i.key_prefix || ''}
                  </div>
                </span>
                {isSel && <Check style={{ width: 12, height: 12, color: 'var(--text-primary)' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Model dropdown sourced from the picked credential's config.models[].
 *  Matches the on-brand dropdown style used everywhere else (button + popover
 *  with hover/selected states, click-outside to close). */
export function EvalModelDropdown({ integration, models: modelsProp, value, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  // Prefer catalog-provided options; fall back to the credential's own list.
  const models = (modelsProp && modelsProp.length) ? modelsProp : (integration?.config?.models || []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!integration) {
    return (
      <div className="input" style={{
        color: 'var(--text-muted)', cursor: 'not-allowed', background: 'var(--surface-sunken)',
        display: 'flex', alignItems: 'center',
      }}>
        Pick a provider first
      </div>
    );
  }
  if (models.length === 0) {
    const manual = MANUAL_MODEL_PROVIDERS.has(integration.provider);
    return (
      <div className="input" style={{ display: 'flex', alignItems: 'center', color: 'var(--text-muted)' }}>
        {manual ? 'No deployments: add them in Integrations.' : 'Loading models…'}
      </div>
    );
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="input"
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', cursor: 'pointer' }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: value ? 'monospace' : 'inherit', color: value ? 'var(--text-primary)' : 'var(--text-muted)' }}>
          {value || 'Select a model'}
        </span>
        <ChevronDown style={{ width: 12, height: 12, color: 'var(--text-muted)', flexShrink: 0 }} />
      </button>
      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-[color:var(--surface-base)] border border-[color:var(--border-default)] rounded-lg shadow-lg"
          style={{ maxHeight: 280, overflowY: 'auto' }}
        >
          {models.map((m) => {
            const isSel = m === value;
            return (
              <button
                key={m}
                type="button"
                onClick={() => { onChange(m); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] font-mono ${isSel ? 'bg-[color:var(--surface-hover)]' : 'hover:bg-[color:var(--surface-hover)]'}`}
              >
                <span className="flex-1 min-w-0 truncate text-[color:var(--text-primary)]">{m}</span>
                {isSel && <Check style={{ width: 12, height: 12, color: 'var(--text-primary)' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
